<template>
  <div v-if="reportLoaded">
    <v-row>
      <v-col md="6" cols="12">
        <v-card>
          <v-card-title>{{ $tc('route_violation', 2)}}</v-card-title>
          <v-card-subtitle>
            <multi-time-display :time="report.parameters.startDate" /> -
            <multi-time-display :time="report.parameters.endDate" />
          </v-card-subtitle>
          <v-card-text>
            <c3-chart
              v-if="showRouteViolationGraph('standard')"
              :height="300"
              type="pie"
              :data="routeViolationGraph.data"
              :config="routeViolationGraph.config" />
            <div v-else class="pie-no-data text-subtitle-1">
              {{ $t('no_data_available') }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col md="6" cols="12">
        <v-card v-if="showComparison">
          <v-card-title>{{ $t('comparison') }}</v-card-title>
          <v-card-subtitle>
            <multi-time-display class="d-inline-block" :time="report.parameters.comparisonStartDate" /> -
            <multi-time-display class="d-inline-block" :time="report.parameters.comparisonEndDate" />
          </v-card-subtitle>
          <v-card-text>
            <c3-chart
              v-if="showRouteViolationGraph('comparison')"
              :height="300"
              type="pie"
              :data="comparisonRouteViolationGraph.data"
              :config="comparisonRouteViolationGraph.config" />
            <div v-else class="pie-no-data text-subtitle-1">
              {{ $t('no_data_available') }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-row class="mb-5">
      <v-col md="6" cols="12">
        <v-card color="grey lighten-5">
          <v-card-title>{{ $tc('root_cause', 2) }}</v-card-title>
          <v-card-subtitle>
            <multi-time-display class="d-inline-block" :time="report.parameters.startDate" /> -
            <multi-time-display class="d-inline-block" :time="report.parameters.endDate" />
          </v-card-subtitle>
          <v-card-text>
            <c3-chart
              v-if="showRootCauseGraph('standard')"
              :height="300"
              type="pie"
              :data="rootCauseGraph.data"
              :config="rootCauseGraph.config" />
            <div v-else class="pie-no-data text-subtitle-1">
              {{ $t('no_data_available') }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col md="6" cols="12">
        <v-card v-if="showComparison" color="grey lighten-5">
          <v-card-title>{{ $t('comparison') }}</v-card-title>
          <v-card-subtitle>
            <multi-time-display class="d-inline-block" :time="report.parameters.comparisonStartDate" /> -
            <multi-time-display class="d-inline-block" :time="report.parameters.comparisonEndDate" />
          </v-card-subtitle>
          <v-card-text>
            <c3-chart
              v-if="showRootCauseGraph('comparison')"
              :height="300"
              type="pie"
              :data="comparisonRootCauseGraph.data"
              :config="comparisonRootCauseGraph.config" />
            <div v-else class="pie-no-data text-subtitle-1">
              {{ $t('no_data_available') }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import ReportRecord from '@/orm/models/ReportRecord'
import C3Chart from '@/components/shared/chart/C3Chart.vue'
import MultiTimeDisplay from '@/components/shared/MultiTimeDisplay'
import { each, get, map, union, zipObject } from 'lodash'

export default {
  name: 'RootCauseReport',
  components: {
    MultiTimeDisplay,
    C3Chart
  },
  props: {
    report: {
      type: ReportRecord
    }
  },
  data () {
    return {
      // Taken from original Yii implementation. These map the c3 defaults to specific indexes so we can map them consistently
      // to labels across both the main and comparison pie graphs.
      rootCauseColorPattern: [
        '#195152',
        '#FF7F0E',
        '#9467BD',
        '#B8D539',
        '#D62728',
        '#709D91',
        '#E377C2',
        '#2CA02C',
        '#7F7F7F',
        '#8C564B',
        '#195152',
        '#FF7F0E',
        '#9467BD',
        '#B8D539',
        '#D62728',
        '#709D91',
        '#E377C2',
        '#2CA02C',
        '#7F7F7F',
        '#8C564B',
        '#195152',
        '#FF7F0E',
        '#9467BD',
        '#B8D539',
        '#D62728',
        '#709D91',
        '#E377C2'
      ]
    }
  },
  computed: {
    /**
     * Identifies if the report is loaded
     * @return boolean
     */
    reportLoaded () {
      return this.report && this.report.results_generated != null
    },
    /**
     * Identify whether to show the comparison graphs
     * @returns {null}
     */
    showComparison () {
      return this.report.parameters.comparisonStartDate && this.report.parameters.comparisonEndDate
    },
    /**
     * C3 graph config for the main route violation graph
     */
    routeViolationGraph () {
      return this.getRouteViolationGraph('standard')
    },
    /**
     * C3 graph config for the main root cause graph
     */
    rootCauseGraph () {
      return this.getRootCauseGraph('standard')
    },
    /**
     * C3 graph config for the comparison route violation graph
     */
    comparisonRouteViolationGraph () {
      return this.getRouteViolationGraph('comparison')
    },
    /**
     * C3 graph config for the comparison root cause graph
     */
    comparisonRootCauseGraph () {
      return this.getRootCauseGraph('comparison')
    },
    /**
     * Build a map from all in-use graph labels to graph colors as defined by rootCauseColorPattern
     */
    rootCauseColorMap () {
      const standardAssessments = map(get(this.report.results, 'standard.root_cause_assessments'), x => x.term_label)
      const comparisonAssessments = map(get(this.report.results, 'comparison.root_cause_assessments'), x => x.term_label)
      return zipObject(union(standardAssessments, comparisonAssessments), this.rootCauseColorPattern)
    }
  },
  methods: {
    showRouteViolationGraph (type) {
      return get(this.report.results, type + '.routes_with_violations.violating') &&
        get(this.report.results, type + '.routes_with_violations.in_spec')
    },
    showRootCauseGraph (type) {
      const assessments = get(this.report.results, type + '.root_cause_assessments')
      return assessments != null && assessments.length > 0
    },
    /**
     * Convenience method to construct the C3 graph data from report results for the route violation graph.
     * @param type The type of graph to construct ('main' or 'comparison')
     */
    getRouteViolationGraph (type) {
      const violating = this.$t('violating')
      const nonViolating = this.$t('non_violating')
      return {
        data: {
          columns: [
            [violating, get(this.report.results, type + '.routes_with_violations.violating')],
            [nonViolating, get(this.report.results, type + '.routes_with_violations.in_spec')]
          ],
          type: 'pie',
          colors: {
            [violating]: '#D9534F',
            [nonViolating]: '#EEEEEE'
          }
        },
        config: {}
      }
    },
    /**
     * Convenience method to construct the C3 graph data from report results for the root cause graph.
     * @param type The type of graph to construct ('main' or 'comparison')
     */
    getRootCauseGraph (type) {
      const columns = []
      each(get(this.report.results, type + '.root_cause_assessments'), item => {
        columns.push([item.term_label, item.value])
      })
      return {
        data: {
          columns: columns,
          type: 'pie',
          color: function (color, d) {
            // this gets called for both the graph itself and the legend, d becomes an object in the legend for
            // some reason, and d.id is the equivalent value.
            return d.id ? this.rootCauseColorMap[d.id] : this.rootCauseColorMap[d]
          }.bind(this)
        },
        config: {}
      }
    }
  }
}
</script>

<style lang="scss" scoped>

.pie-no-data {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
  min-height: 300px;
}
</style>
